/**
 * @fileoverview Review Evidence Phase Component
 * @module ReviewEvidencePhase
 * 
 * Business Context:
 * - First phase of the Build Your Case wizard
 * - Clean, scannable summary of collected evidence
 * - Consistent navigation pattern with other phases
 */

import React, { useState } from 'react';
import {
  ClipboardCheck,
  DollarSign,
  Home,
  Layers,
  Wind,
  Droplets,
  Zap,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  MapPin
} from 'lucide-react';
import { AppealSummary } from '../BuildYourCaseWizard';

interface ReviewEvidencePhaseProps {
  summary: AppealSummary | null;
  hasEvidence: boolean;
  onContinue: () => void;
}

const categoryIcons: Record<string, React.ReactNode> = {
  ROOF: <Home className="w-4 h-4" />,
  FOUNDATION: <Layers className="w-4 h-4" />,
  HVAC: <Wind className="w-4 h-4" />,
  PLUMBING: <Droplets className="w-4 h-4" />,
  ELECTRICAL: <Zap className="w-4 h-4" />,
};

const categoryLabels: Record<string, string> = {
  ROOF: 'Roof',
  FOUNDATION: 'Foundation',
  HVAC: 'HVAC',
  PLUMBING: 'Plumbing',
  ELECTRICAL: 'Electrical'
};

const FLAW_LABELS: Record<string, string> = {
  WRONG_DETAILS: 'Property Details',
  QUALITY_MISCLASSIFICATION: 'Quality Rating',
  CONDITION_MISCLASSIFICATION: 'Condition Rating',
  OUTDATED_SALES: 'Sales Data'
};

const formatPropertyType = (type: string): string => {
  const typeMap: Record<string, string> = {
    'single_family': 'Single Family',
    'condo': 'Condo',
    'townhouse': 'Townhouse',
    'mobile_home': 'Mobile Home',
    'multi_family': 'Multi-Family',
  };
  return typeMap[type?.toLowerCase()] || type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Residential';
};

const ReviewEvidencePhase: React.FC<ReviewEvidencePhaseProps> = ({
  summary,
  hasEvidence,
  onContinue
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toLocaleString()}`;
  };

  const formatCurrencyDollars = (dollars: number): string => {
    return `$${dollars.toLocaleString()}`;
  };

  // No evidence warning
  if (!hasEvidence) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No Evidence Collected Yet</h3>
            <p className="text-slate-500 mb-5">
              Complete Steps 1 and 3 to document your property conditions and build your evidence.
            </p>
            <div className="flex gap-3">
              <a
                href="/step/1/document"
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                Document Conditions
              </a>
              <a
                href="/step/3/find-flaws"
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
              >
                Review Evidence
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Property Card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Address Row */}
        <div className="p-5 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-slate-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{summary?.property.address}</h3>
              <p className="text-sm text-slate-500">
                {summary?.property.city}, {summary?.property.state} {summary?.property.zipCode}
              </p>
            </div>
          </div>
        </div>
        
        {/* Property Details Row */}
        <div className="px-5 pb-5">
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="grid grid-cols-3 divide-x divide-slate-200">
              <div className="px-3 first:pl-0">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Type</p>
                <p className="text-sm font-semibold text-slate-800">
                  {formatPropertyType(summary?.property.propertyType || '')}
                </p>
              </div>
              <div className="px-3">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Year Built</p>
                <p className="text-sm font-semibold text-slate-800">
                  {summary?.property.yearBuilt || '—'}
                </p>
              </div>
              <div className="px-3 last:pr-0">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Size</p>
                <p className="text-sm font-semibold text-slate-800">
                  {summary?.property.squareFootage 
                    ? `${summary.property.squareFootage.toLocaleString()} sqft`
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Property Conditions */}
        {summary?.maintenanceSummary && summary.maintenanceSummary.totalIssues > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <ClipboardCheck className="w-4 h-4" />
              <span className="text-sm font-medium">Conditions Documented</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 mb-2">
              {summary.maintenanceSummary.totalIssues}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {summary.maintenanceSummary.categories.map((cat) => (
                <span
                  key={cat.category}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600"
                >
                  {categoryIcons[cat.category]}
                  {categoryLabels[cat.category]}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Repair Costs */}
        {summary?.maintenanceSummary && summary.maintenanceSummary.totalEstimatedCost > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm font-medium">Estimated Repairs</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">
              {formatCurrency(summary.maintenanceSummary.totalEstimatedCost)}
            </p>
          </div>
        )}
      </div>

      {/* Expandable Details */}
      {(summary?.maintenanceSummary?.categories?.length || summary?.flawSummary?.flaws?.length) && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
          >
            <span className="font-medium text-slate-700">View Evidence Details</span>
            {showDetails ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {showDetails && (
            <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
              {/* Condition Categories */}
              {summary?.maintenanceSummary?.categories && summary.maintenanceSummary.categories.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-3">Property Conditions</h4>
                  <div className="space-y-2">
                    {summary.maintenanceSummary.categories.map((cat, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500">{categoryIcons[cat.category]}</span>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{categoryLabels[cat.category]}</p>
                            <p className="text-xs text-slate-500">{cat.issueCount} issues</p>
                          </div>
                        </div>
                        <p className="font-medium text-slate-700 text-sm">{formatCurrency(cat.estimatedCost)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Valuation Considerations */}
              {summary?.flawSummary?.flaws && summary.flawSummary.flaws.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-3">Valuation Considerations</h4>
                  <div className="space-y-2">
                    {summary.flawSummary.flaws.map((flaw, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-slate-900 text-sm">
                            {FLAW_LABELS[flaw.flawType] || flaw.flawLabel}
                          </p>
                          <span className="text-xs text-teal-600 font-medium">
                            {flaw.issueCount} item{flaw.issueCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {flaw.checkedItems.length > 0 && (
                          <ul className="space-y-1">
                            {flaw.checkedItems.slice(0, 2).map((item, iIndex) => (
                              <li key={iIndex} className="flex items-start gap-2 text-xs text-slate-600">
                                <CheckCircle2 className="w-3 h-3 text-teal-500 flex-shrink-0 mt-0.5" />
                                <span className="line-clamp-1">{item}</span>
                              </li>
                            ))}
                            {flaw.checkedItems.length > 2 && (
                              <li className="text-xs text-slate-400 pl-5">
                                +{flaw.checkedItems.length - 2} more
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Navigation Bar - Consistent across phases */}
      <div className="bg-teal-600 rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {summary?.reductionBreakdown && summary.reductionBreakdown.target > 0 && (
              <>
                <p className="text-teal-100 text-sm">Suggested Reduction</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrencyDollars(summary.reductionBreakdown.target)}
                </p>
              </>
            )}
          </div>
          <button
            onClick={onContinue}
            className="px-5 py-2.5 bg-white text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition-colors flex items-center gap-2"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewEvidencePhase;
